import React from 'react';
import { Box, Card, Stack, Typography } from '@mui/material';
import TideArrow from 'components/Icons/TideArrow';
import ErrorBoundary from 'components/ErrorBoundary';
import type { ForecastTide } from 'types/spot';
import type { Units } from 'types/units';
import CurrentTideGraph from './components/CurrentTideGraph';

import styles from './CurrentTideConditions.module.scss';

interface CurrentTideContentsProps {
  tide: ForecastTide;
  units?: Units;
}

const CurrentTideContents: React.FC<CurrentTideContentsProps> = ({ tide, units }) => {
  if (!tide || Object.keys(tide).length === 0 || !units || Object.keys(units).length === 0) {
    return (
      <Typography variant="callout1" color="text.secondary" className={styles.noData}>
        No data
      </Typography>
    );
  }

  const tideIcon = (nextTideType: string, currentTideType: string) => {
    if (currentTideType === 'HIGH' || currentTideType === 'LOW') {
      return null;
    }
    switch (nextTideType) {
      case 'HIGH':
        return <TideArrow direction="rising" />;
      case 'LOW':
        return <TideArrow direction="falling" />;
      default:
        return null;
    }
  };

  const { current, next } = tide;

  const roundedCurrentHeight = current?.height?.toFixed(1);

  return (
    <Box>
      <Box className={styles.heightWrapper}>
        <Stack direction="row" alignItems="baseline" data-testid="current-tide-height-units">
          <Typography variant="title2" component="span" className={styles.value}>
            {roundedCurrentHeight}
          </Typography>
          <Typography variant="headline" component="span" className={styles.units}>
            {units?.tideHeight}
          </Typography>
        </Stack>
        <Box>{tideIcon(next?.type, current?.type)}</Box>
      </Box>
      <Box>
        <CurrentTideGraph />
      </Box>
    </Box>
  );
};

interface CurrentTideProps extends CurrentTideContentsProps {
  error?: boolean;
}

export const CurrentTideConditions: React.FC<CurrentTideProps> = (props) => {
  const { error } = props;
  return (
    <Box data-testid="current-tide-conditions-card">
      <Card className={styles.cardContent}>
        <ErrorBoundary
          error={error}
          errorBoundaryId="current-tide"
          render={() => (
            <Stack
              className={styles.error}
              direction="row"
              justifyContent="center"
              alignItems="center"
              data-testid="current-tide-conditions-error"
            >
              <Typography variant="callout1" className={styles.errorText}>
                Something went wrong here.
              </Typography>
            </Stack>
          )}
        >
          <CurrentTideContents {...props} />
        </ErrorBoundary>
      </Card>
    </Box>
  );
};

export default CurrentTideConditions;
